import React, { useState, useEffect, useReducer } from 'react'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import {
    processFirstWordsText,
    processLastWordsText,
    processFirstWordsAfterFirstNumberOfWordsText,
    processFirstWordsAfterLastNumberOfWordsText,
    processFirstWordsBeforeOccurence,
    processRegularExpressionText,
    processDateExpression
} from './Extractors'

function MeasuresDefinition(props) {
    const [dimensions, setDimensions] = useState([])
    const [builtMeasures, setBuiltMeasures] = useState([])
    const [userMeasures, setUserMeasures] = useState([])
    const [aggregationSelections, setAggregationSelections] = useState([])
    const [tableHasHeader, setTableHasHeader] = useState(false)
    const [selectedIndividualCells, setSelectedIndividualCells] = useState([])
    const [, forceUpdate] = useReducer(x => x + 1, 0)

    const NEW_MEASURE_STRING = "New Measure"

    // trigger on component mount
    useEffect(() => {
        setDimensions(props.dimensions)
        setTableHasHeader(props.tableHasHeader)
        
        let offset = getTabsRowOffset(props.tableHasHeader, props.previewData[0]["previewData"])

        if (props.aggregationSelections && props.aggregationSelections.length !== aggregationSelections.length) {
            buildMeasuresUp(props.previewData, props.dimensions, props.factsColumnRanges, offset, props.aggregationSelections)
        } else {
            buildMeasuresUp(props.previewData, props.dimensions, props.factsColumnRanges, offset, [])
        }
    }, [])

    useEffect(() => {
        if (props.tableHasHeader !== tableHasHeader) {
            setTableHasHeader(props.tableHasHeader)

            let offset = getTabsRowOffset(props.tableHasHeader, props.previewData[0]["previewData"])

            if (props.aggregationSelections && props.aggregationSelections.length !== aggregationSelections.length) {
                buildMeasuresUp(props.previewData, props.dimensions, props.factsColumnRanges, offset, props.aggregationSelections)
            } else {
                buildMeasuresUp(props.previewData, props.dimensions, props.factsColumnRanges, offset, [])
            }
        }
    }, [props.tableHasHeader])

    function renderDimensionHeaders(dimensionDefinitions) {
        let elements = []
        for (let x = 0; x < dimensionDefinitions.length; x++) {
            if (dimensionDefinitions[x]["id"] !== -1) {
                elements.push(<TableCell key={`dimension-header-prop-${dimensionDefinitions[x]["id"]}`}>{dimensionDefinitions[x]["name"]}</TableCell>)
            }
        }
        return elements
    }

    function renderDimensionDropdowns(dimensionDefinitions, measureData, rowIndex, mandatroyMeasures, selected, allMeasures) {
        let elements = []
        let weightedAverageDropDowns = []

        for (let x = 0; x < mandatroyMeasures.length; x++) {
            if (measureData !== mandatroyMeasures[x]) {
                weightedAverageDropDowns.push(<MenuItem key={`weighted-measure-${x}`} value={mandatroyMeasures[x]}>Weighted Average: {mandatroyMeasures[x]}</MenuItem>)
            }
        }

        for (let x = 0; x < dimensionDefinitions.length; x++) {
            if (dimensionDefinitions[x]["id"] !== -1) {
                elements.push(<TableCell
                    key={`dimension-header-row-${rowIndex}-prop-${dimensionDefinitions[x]["id"]}`}
                    style={{ "cursor": "pointer", "backgroundColor": getBackgroundColorForCellIfSelected(rowIndex, (x+1), selected) }}
                    onClick={(e) => {
                        e.preventDefault()
                        changeSelectedCells(rowIndex, (x+1))
                    }}>
                    <Select
                        value={getAggregationType(dimensionDefinitions[x], aggregationSelections, measureData)}
                        id={`select-dimension-header-row-${rowIndex}-prop-${dimensionDefinitions[x]["id"]}`}
                        fullWidth
                        size="small"
                        onClick={(event) => event.stopPropagation()}
                        onChange={(e) => {
                            e.stopPropagation()
                            let copiedAggregationSelections = changeMultipleAggregationTypes(e.target.value, dimensionDefinitions[x], aggregationSelections, measureData, dimensionDefinitions, allMeasures, selected)
                            setAggregationSelections(copiedAggregationSelections)
                            setSelectedIndividualCells([])
                            forceUpdate()

                            if (props.onSave) {
                                props.onSave({
                                    "builtMeasures": builtMeasures,
                                    "aggregationSelections": copiedAggregationSelections,
                                    "userMeasures": userMeasures
                                })
                            }
                        }}>
                        <MenuItem value={0}>Undefined</MenuItem>
                        <MenuItem value={1}>Sum</MenuItem>
                        <MenuItem value={2}>Count</MenuItem>
                        <MenuItem value={3}>Min</MenuItem>
                        <MenuItem value={4}>Max</MenuItem>
                        <MenuItem value={5}>Average</MenuItem>
                        {weightedAverageDropDowns}
                    </Select>
                </TableCell>)
            }
        }
        return elements
    }

    function buildMeasuresUp(previewData, dimensionDefinitions, factsCols, headingRow, passedSelections) {
        if (previewData.length > 0) {
            let dataGrab = []
            let built = []
            let customerDefinedMeasures = []

            let measuresAxis = null
            for (let x = 0; x < dimensionDefinitions.length; x++) {
                if (dimensionDefinitions[x]["id"] === -1) {
                    measuresAxis = dimensionDefinitions[x]
                }
            }
            
            if (measuresAxis !== null) {
                for (let tabIndex = 0; tabIndex < previewData.length; tabIndex++) {
                    dataGrab = previewData[tabIndex]["previewData"]
                    let tabIndexMeasuresBuilt = []

                    // We need to check for any column sources first
                    let containsColumnRanges = false, containsIndividualCellRanges = false
                    for (let x = 0; x < measuresAxis["ranges"].length; x++) {
                        if (measuresAxis["ranges"][x]["colIndex"] > -20 || measuresAxis["ranges"][x]["name"].includes('NColumn:')) {
                            if (measuresAxis["ranges"][x]["rowIndex"] > -20) {
                                containsIndividualCellRanges = true
                            } else {
                                containsColumnRanges = true
                            }
                        }
                    }

                    // If any column ranges exists, we need to create a measure name for each row in the data as it influences a new measure name
                    //? All of this for a scenario that is rarely if ever used, ugh!
                    if (containsColumnRanges) {
                        let copiedOutput = measuresAxis["outputFormat"].join('')
                        for (let y = 0; y < dataGrab.length; y++) {
                            let currentlyBuiltName = copiedOutput
                            for (let x = 0; x < measuresAxis["ranges"].length; x++) {
                                if (measuresAxis["ranges"][x]["colIndex"] > -20 || measuresAxis["ranges"][x]["name"].includes('NColumn:')) {
                                    if (measuresAxis["ranges"][x]["colIndex"] > -20) {
                                        currentlyBuiltName = currentlyBuiltName.replace(`(Column:${getExcelColumnName(measuresAxis["ranges"][x]["colIndex"]+1)})`, getColumnValue(headingRow, y, dataGrab, measuresAxis["ranges"][x]["colIndex"], measuresAxis["ranges"][x]["name"]))
                                    }
                                    if (measuresAxis["ranges"][x]["name"].includes('NColumn:')) {
                                        currentlyBuiltName = currentlyBuiltName.replace(`(${measuresAxis["ranges"][x]["name"]})`, getColumnValue(headingRow, y, dataGrab, measuresAxis["ranges"][x]["colIndex"], measuresAxis["ranges"][x]["name"]))
                                    }
                                }

                                if (measuresAxis["ranges"][x]["useProperCase"]) {
                                    currentlyBuiltName = toProperCase(currentlyBuiltName)
                                }
                                let extractor = measuresAxis["ranges"][x]["extractor"]
                                currentlyBuiltName = processText(currentlyBuiltName, extractor["extractorType"], extractor["numberOfWords"], extractor["inverse"], extractor["after"], extractor["occurrence"], extractor["regularExpressionTerm"], extractor["regularExpressionOutput"], extractor["extractionType"])
                            }
                            if (currentlyBuiltName.length > 0) {
                                tabIndexMeasuresBuilt.push(currentlyBuiltName)
                            }
                        }
                    }

                    // We need to build up measure parts that contain individual cells
                    if (containsIndividualCellRanges) {
                        let copiedOutput = measuresAxis["outputFormat"].join('')
                        for (let y = 0; y < dataGrab.length; y++) {
                            let currentlyBuiltName = copiedOutput
                            for (let x = 0; x < measuresAxis["ranges"].length; x++) {
                                if (measuresAxis["ranges"][x]["colIndex"] > -20 && measuresAxis["ranges"][x]["rowIndex"] > -20) {
                                    currentlyBuiltName = currentlyBuiltName.replace(`(PCell ${getExcelColumnName(measuresAxis["ranges"][x]["colIndex"]+1)}${measuresAxis["ranges"][x]["rowIndex"]+1})`, getValueFromIndividualCell(dataGrab, measuresAxis["ranges"][x]["rowIndex"], measuresAxis["ranges"][x]["colIndex"]))
                                }

                                if (measuresAxis["ranges"][x]["useProperCase"]) {
                                    currentlyBuiltName = toProperCase(currentlyBuiltName)
                                }
                                let extractor = measuresAxis["ranges"][x]["extractor"]
                                currentlyBuiltName = processText(currentlyBuiltName, extractor["extractorType"], extractor["numberOfWords"], extractor["inverse"], extractor["after"], extractor["occurrence"], extractor["regularExpressionTerm"], extractor["regularExpressionOutput"], extractor["extractionType"])
                            }
                            if (currentlyBuiltName.length > 0) {
                                tabIndexMeasuresBuilt.push(currentlyBuiltName)
                            }
                        }
                    }
                    
                    // Look into the facts soruce column definitions, we need to get the col indexes for
                    let colIndexes = []
                    for (let x = 0; x < factsCols.length; x++) {
                        if (!factsCols[x]["absolute"]) {
                            let newColIndex = getColumnIndexFromName(headingRow, dataGrab, factsCols[x]["name"])
                            colIndexes.push(newColIndex)
                        } else if (factsCols[x]["leftIsAnchored"] && factsCols[x]["rightIsAnchored"]) {
                            for (let definitionIndex = factsCols[x]["left"]; definitionIndex <= factsCols[x]["right"]; definitionIndex++) {
                                colIndexes.push(definitionIndex)
                            }
                        }  else if  (factsCols[x]["leftIsAnchored"]) {
                            let anchor = factsCols[x]["left"]
                            let width = factsCols[x]["right"] - factsCols[x]["left"]
                            let itr = 0
                            
                            while (itr <= width) {
                                colIndexes.push(anchor)
                                anchor++
                                itr++
                            }
                        } else {
                            let rAnchor = factsCols[x]["tableWidth"] - factsCols[x]["right"]
                            let width = factsCols[x]["right"] - factsCols[x]["left"] + 1
                            let itr = 0
                            
                            while (itr < width) {
                                colIndexes.push(rAnchor)
                                rAnchor--
                                itr++
                            }
                        }
                    }

                    // Check first if we're in the middle of building measures from columns, keep building off that or go ahead and get new definitions built from the row source
                    // We can assume there are no column based indexes here
                    if (tabIndexMeasuresBuilt.length === 0) {
                        let copiedOutput = measuresAxis["outputFormat"].join('')
                        for (let x = 0; x < measuresAxis["ranges"].length; x++) {
                            if (measuresAxis["ranges"][x]["rowIndex"] > -20 && measuresAxis["ranges"][x]["colIndex"] <= -20) {
                                for (let y = 0; y < colIndexes.length; y++) {
                                    let currentlyBuiltName = copiedOutput
                                    currentlyBuiltName = currentlyBuiltName.replace(`(Row:${measuresAxis["ranges"][x]["rowIndex"]+1})`, getColumnValue(-1, measuresAxis["ranges"][x]["rowIndex"]+headingRow, dataGrab, colIndexes[y], ''))
                                    if (measuresAxis["ranges"][x]["useProperCase"]) {
                                        currentlyBuiltName = toProperCase(currentlyBuiltName)
                                    }
                                    let extractor = measuresAxis["ranges"][x]["extractor"]
                                    currentlyBuiltName = processText(currentlyBuiltName, extractor["extractorType"], extractor["numberOfWords"], extractor["inverse"], extractor["after"], extractor["occurrence"], extractor["regularExpressionTerm"], extractor["regularExpressionOutput"], extractor["extractionType"])
                                    if (currentlyBuiltName.length > 0) {
                                        tabIndexMeasuresBuilt.push(currentlyBuiltName)
                                    }
                                }
                            }
                        }
                    } else {
                        let newMeasureBuildUp = []
                        for (let x = 0; x < built.length; x++) {
                            for (let y = 0; y < measuresAxis["ranges"].length; y++) {
                                if (measuresAxis["ranges"][y]["rowIndex"] > -20 && measuresAxis["ranges"][y]["colIndex"] === -20) {
                                    for (let z = 0; z < colIndexes.length; z++) {
                                        let currentlyBuiltName = built[x]
                                        currentlyBuiltName = built[x].replace(`(Row:${measuresAxis["ranges"][y]["rowIndex"]+1})`, getColumnValue(-1, measuresAxis["ranges"][y]["rowIndex"]+headingRow, dataGrab, colIndexes[z], ''))
                                        if (currentlyBuiltName.length > 0) {
                                            newMeasureBuildUp.push(currentlyBuiltName)
                                        }
                                    }
                                }
                            }
                        }
                        if (newMeasureBuildUp.length > 0) {
                            tabIndexMeasuresBuilt = newMeasureBuildUp
                        }
                    }

                    built = built.concat(tabIndexMeasuresBuilt)
                }

                built = removeDuplicates(built)
                setBuiltMeasures(built)

                // Build up the aggregations
                let aggregations = []

                // If we are building from scratch
                if (passedSelections.length === 0) {
                    for (let x = 0; x < dimensionDefinitions.length; x++) {
                        if (dimensionDefinitions[x]["id"] !== -1) {
                            for (let y = 0; y < built.length; y++) {
                                let currentDimension = dimensionDefinitions[x]
                                aggregations.push({
                                    "dimensionId": currentDimension["id"],
                                    "dimensionName": currentDimension["name"],
                                    "dimensionType": currentDimension["type"],
                                    "aggregationType" : 0,
                                    "measure": built[y],
                                    "format": "",
                                    "calcParameters": ""
                                })
                            }
                        }
                    }
                } else {
                    for (let x = 0; x < passedSelections.length; x++) {
                        if (!built.includes(passedSelections[x]["measure"]) && !customerDefinedMeasures.includes(passedSelections[x]["measure"])) {
                            customerDefinedMeasures.push(passedSelections[x]["measure"])
                        }
                    }

                    // Sometimes the dimension name will change but dimension type remains the same, make sure that persists
                    for (let dimensionIndex = 0; dimensionIndex < dimensionDefinitions.length; dimensionIndex++) {
                        let currentDimension = dimensionDefinitions[dimensionIndex]
                        for (let passedSelectionIndex = 0; passedSelectionIndex < passedSelections.length; passedSelectionIndex++) {
                            let currentSelection = passedSelections[passedSelectionIndex]
                            if (currentDimension["type"] === currentSelection["dimensionType"]) {
                                currentSelection["dimensionName"] = currentDimension["name"]
                            }
                        }
                    }

                    aggregations = passedSelections
                    setUserMeasures(customerDefinedMeasures)
                }
                setAggregationSelections(aggregations)

                if (props.onSave) {
                    props.onSave({
                        "builtMeasures": built,
                        "aggregationSelections": aggregations,
                        "userMeasures": customerDefinedMeasures
                    })
                }
            }
        }
    }

    function getColumnValue(headingRow, rowIndex, data, colIndex, columName) {
        let calculatedColumnIndex = colIndex
        let processedColumnName = columName.replace('NColumn:', '')
        if (calculatedColumnIndex === -20 && data[headingRow]) {
            for (let x = 0; x < data[headingRow].length; x++) {
                if (processedColumnName === data[headingRow][x]) {
                    calculatedColumnIndex = x
                }
            }
        }

        if (data[rowIndex][calculatedColumnIndex]) {
            return data[rowIndex][calculatedColumnIndex]
        }

        return ''
    }

    function getValueFromIndividualCell(data, rowIndex, colIndex) {
        return data[rowIndex][colIndex]
    }

    /**
     * getColumnIndexFromName
     * * Looks up the exact column index from the column name
     * @param {int} headingRow - Row index that represents the header 
     * @param {Array} data - Preview data from the source 
     * @param {string} columName - Name of column, usually in the format of [NColumn:{Any name}] 
     * @returns 
     */
    function getColumnIndexFromName(headingRow, data, columName) {
        let rtnVal = -20
        let processedColumnName = columName.replace('NColumn:', '')
        for (let x = 0; x < data[headingRow].length; x++) {
            if (processedColumnName === data[headingRow][x]) {
                rtnVal = x
            }
        }

        return rtnVal
    }

    function getExcelColumnName(columnNumber) {
        let columnName = ""
    
        while (columnNumber > 0) {
            const modulo = (columnNumber - 1) % 26
            columnName = String.fromCharCode('A'.charCodeAt(0) + modulo) + columnName
            columnNumber = Math.floor((columnNumber - modulo) / 26)
        }
    
        return columnName
    }

    function removeDuplicates(array) {
        let uniqueArray = [];
        for (let i = 0; i < array.length; i++) {
            if (!uniqueArray.includes(array[i])) {
                uniqueArray.push(array[i]);
            }
        }
        return uniqueArray;
    }

    function changeAggregationType(newAggType, dimensionData, selections, measureName) {
        let copiedSelections = selections
        
        if (typeof newAggType === 'number' && Number.isInteger(newAggType)) { 
            for (let x = 0; x < copiedSelections.length; x++) {
                if (copiedSelections[x]["measure"] === measureName &&
                    copiedSelections[x]["dimensionName"] === dimensionData["name"] &&
                    copiedSelections[x]["dimensionType"]  === dimensionData["type"]) {
                        copiedSelections[x]["aggregationType"] = newAggType
                        copiedSelections[x]["calcParameters"] = ''
                }
            }
        } else {
            for (let x = 0; x < copiedSelections.length; x++) {
                if (copiedSelections[x]["measure"] === measureName &&
                    copiedSelections[x]["dimensionName"] === dimensionData["name"] &&
                    copiedSelections[x]["dimensionType"]  === dimensionData["type"]) {
                        copiedSelections[x]["aggregationType"] = 6
                        copiedSelections[x]["calcParameters"] = newAggType
                }
            }
        }

        return copiedSelections
    }

    function changeMultipleAggregationTypes(newAggType, dimensionData, selections, measureName, allDimensions, allMeasures, additionalSelectedCells) {
        let copiedSelections = changeAggregationType(newAggType, dimensionData, selections, measureName)

        additionalSelectedCells.forEach((selectedCell) => {
            let dimensionIndex = selectedCell["column"]-1
            let measureIndex = selectedCell["row"]
            copiedSelections = changeAggregationType(newAggType, allDimensions[dimensionIndex], copiedSelections, allMeasures[measureIndex])
        })

        return copiedSelections
    }

    function changeFormatString(newFormatString, selections, measureName) {
        let copiedSelections = selections

        for (let x = 0; x < copiedSelections.length; x++) {
            if (copiedSelections[x]["measure"] === measureName) {
                copiedSelections[x]["format"] = newFormatString
            }
        }

        return copiedSelections
    }

    function getAggregationType(dimensionData, selections, measureName) {
        let rtnVal = 0

        for (let x = 0; x < selections.length; x++) {
            if (selections[x]["measure"] === measureName &&
                selections[x]["dimensionName"] === dimensionData["name"] &&
                selections[x]["dimensionType"]  === dimensionData["type"]) {
                    if (selections[x]["aggregationType"] === 6) {
                        rtnVal = selections[x]["calcParameters"]
                    } else {
                        rtnVal = selections[x]["aggregationType"]
                    }
            }
        }


        return rtnVal
    }

    function getFormatString(selections, measureName) {
        let rtnVal = ''

        for (let x = 0; x < selections.length; x++) {
            if (selections[x]["measure"] === measureName) {
                    rtnVal = selections[x]["format"]
            }
        }


        return rtnVal
    }

    function getMandaryRowBackgroundColor(measureName, selections) {
        let isDefined = true

        for (let x = 0; x < selections.length; x++) {
            if (selections[x]["measure"] === measureName && selections[x]["aggregationType"] === 0) {
                isDefined = false
            }
        }

        if (isDefined) {
            return props.uiColors.MANDATORY_DEFINED
        }
        return props.uiColors.MANDATORY_UNDEFINED
    }

    function getUserDefinedRowBackgroundColor(measureName, selections) {
        let isDefined = true

        for (let x = 0; x < selections.length; x++) {
            if (selections[x]["measure"] === measureName && selections[x]["aggregationType"] === 0) {
                isDefined = false
            }
        }

        if (isDefined) {
            return props.uiColors.USER_DEFINED
        }
        return props.uiColors.USER_UNDEFINED
    }

    /**
     * getTabsRowOffset
     * * 
     * @param {boolean} hasHeader - Do the tables have a header? 
     * @param {Array} tabPreviewData - Preview data from the source 
     * @returns {int} First row header
     */
    function getTabsRowOffset(hasHeader, tabPreviewData) {
        // Only run this if we determine the table has a header
        if (hasHeader) {
            for (let x = 0; x < tabPreviewData.length; x++) {
                if (props.doesThisRowHaveDataInEverySingleColumn(tabPreviewData[x])) {
                    return x
                }
            }
        }

        return 0
    }

    function toProperCase(inputString) {
        let words = inputString.toLowerCase().split(' ')
        for (let i = 0; i < words.length; i++) {
            words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1)
        }
    
        return words.join(' ')
    }

    function processText(text, type, words, inverseSelection, afterWords, stringOccurence, expressionTerm, expressionOutput, dateType) {
        if (type === 'FIRSTWORDS') {
            return processFirstWordsText(text, words, inverseSelection)
        } else if (type === 'LASTWORDS') {
            return processLastWordsText(text, words, inverseSelection)
        } else if (type === 'FIRSTWORDSAFTERFIRSTNUMBEROFWORDS') {
            return processFirstWordsAfterFirstNumberOfWordsText(text, words, inverseSelection, afterWords)
        } else if (type === 'FIRSTWORDSAFTERLASTNUMBEROFWORDS') {
            return processFirstWordsAfterLastNumberOfWordsText(text, words, inverseSelection, afterWords)
        } else if (type === 'FIRSTWORDSBEFOREOCCURRENCE') {
            return processFirstWordsBeforeOccurence(text, words, inverseSelection, stringOccurence)
        } else if (type === 'REGULAREXPRESSION') {
            return processRegularExpressionText(text, expressionTerm, expressionOutput)
        } else if (type === 'ENDDATE') {
            processDateExpression(text, dateType)
        }

        return text
    }

    function changeSelectedCells(row, col) {
        setSelectedIndividualCells((prevCells) => {
            const exists = prevCells.some(cell => cell.row === row && cell.column === col)
          
            if (exists) {
                return prevCells.filter(cell => !(cell.row === row && cell.column === col))
            } else {
                return [...prevCells, { row, column: col }]
            }
        })
    }
    
    function getBackgroundColorForCellIfSelected(row, col, selected) {
        const isSelected = selected.some(s => s.row === row && s.column === col)
        return isSelected ? props.uiColors.GRID_HIGHLIGHT : "transparent"
    }

    return <>
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        {renderDimensionHeaders(dimensions)}
                        <TableCell>Format</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {builtMeasures.map((measure, mIndex) => {
                        return <TableRow key={`measure-row-${mIndex}`} sx={{ "backgroundColor": getMandaryRowBackgroundColor(measure, aggregationSelections) }}>
                            <TableCell>
                                {measure}
                            </TableCell>
                            {renderDimensionDropdowns(dimensions, measure, mIndex, builtMeasures, selectedIndividualCells, builtMeasures.concat(userMeasures))}
                            <TableCell>
                                <Select
                                    value={getFormatString(aggregationSelections, measure)}
                                    id={`select-measure-format-${mIndex}`}
                                    fullWidth
                                    size="small"
                                    onChange={(e) => {
                                        e.preventDefault()
                                        let copiedFormatStringSelections = changeFormatString(e.target.value, aggregationSelections, measure)
                                        setAggregationSelections(copiedFormatStringSelections)
                                        forceUpdate()

                                        if (props.onSave) {
                                            props.onSave({
                                                "builtMeasures": builtMeasures,
                                                "aggregationSelections": copiedFormatStringSelections,
                                                "userMeasures": userMeasures
                                            })
                                        }
                                    }}
                                >
                                    <MenuItem value={''}></MenuItem>
                                    <MenuItem value={'0'}>0</MenuItem>
                                    <MenuItem value={'0.0'}>0.0</MenuItem>
                                    <MenuItem value={'0.00'}>0.00</MenuItem>
                                    <MenuItem value={'#,##0'}>#,##0</MenuItem>
                                    <MenuItem value={'#,##0.0'}>#,##0.0</MenuItem>
                                    <MenuItem value={'#,##0.0'}>#,##0.00</MenuItem>
                                    <MenuItem value={'#,##0;(#,##0)'}>#,##0;(#,##0)</MenuItem>
                                    <MenuItem value={'#,##0.00;(#,##0.00)'}>#,##0.00;(#,##0.00)</MenuItem>
                                    <MenuItem value={'$#,##0;($#,##0)'}>$#,##0;($#,##0)</MenuItem>
                                    <MenuItem value={'$#,##0.00;($#,##0.00)'}>$#,##0.00;($#,##0.00)</MenuItem>
                                    <MenuItem value={'0%'}>0%</MenuItem>
                                    <MenuItem value={'0.0%'}>0.0%</MenuItem>
                                    <MenuItem value={'0.00%'}>0.00%</MenuItem>
                                    <MenuItem value={'0"%"'}>0"%"</MenuItem>
                                    <MenuItem value={'0.0"%"'}>0.0"%"</MenuItem>
                                    <MenuItem value={'0.00"%"'}>0.00"%"</MenuItem>
                                </Select>
                            </TableCell>
                        </TableRow>
                    })}
                    {userMeasures.map((measure, mIndex) => {
                        return <TableRow key={`user-measure-row-${mIndex}`} sx={{ "backgroundColor": getUserDefinedRowBackgroundColor(measure, aggregationSelections) }}>
                            <TableCell>
                                <TextField
                                    label=""
                                    value={measure}
                                    size="small"
                                    onChange={(e) => {
                                        e.preventDefault()

                                        let newMeasureValue = e.target.value
                                        let copiedUserMeasures = userMeasures
                                        let copiedAggregationSelections = aggregationSelections

                                        copiedUserMeasures[mIndex] = newMeasureValue
                                        let dimensionChangeMaxThreshold = dimensions.length - 1
                                        let timesChanged = 0
                                        
                                        for (let x = 0; x < copiedAggregationSelections.length; x++) {
                                            if (copiedAggregationSelections[x]["measure"] === measure && timesChanged < dimensionChangeMaxThreshold) {
                                                copiedAggregationSelections[x]["measure"] = newMeasureValue
                                                timesChanged++
                                            }
                                        }

                                        setAggregationSelections(copiedAggregationSelections)
                                        setUserMeasures(copiedUserMeasures)
                                        forceUpdate()

                                        if (props.onSave) {
                                            props.onSave({
                                                "builtMeasures": builtMeasures,
                                                "aggregationSelections": copiedAggregationSelections,
                                                "userMeasures": copiedUserMeasures
                                            })
                                        }
                                    }}
                                />
                                <IconButton
                                    aria-label="delete"
                                    onClick={(e) => {
                                        e.preventDefault()

                                        let copiedUserMeasures = userMeasures
                                        let copiedAggregationSelections = aggregationSelections
                                        let newBuiltUserMeasures = []
                                        let newlyBuiltAggregationSelections = []

                                        for (let x = 0; x < copiedAggregationSelections.length; x++) {
                                            if (copiedAggregationSelections[x]["measure"] !== measure) {
                                                newlyBuiltAggregationSelections.push(copiedAggregationSelections[x])
                                            }
                                        }
                                        for (let x = 0; x < copiedUserMeasures.length; x++) {
                                            if (copiedUserMeasures[x] !== measure) {
                                                newBuiltUserMeasures.push(copiedUserMeasures[x])
                                            }
                                        }

                                        setAggregationSelections(newlyBuiltAggregationSelections)
                                        setUserMeasures(newBuiltUserMeasures)
                                        forceUpdate()

                                        if (props.onSave) {
                                            props.onSave({
                                                "builtMeasures": builtMeasures,
                                                "aggregationSelections": newlyBuiltAggregationSelections,
                                                "userMeasures": newBuiltUserMeasures
                                            })
                                        }
                                    }}>
                                    <DeleteIcon />
                                </IconButton>
                            </TableCell>
                            {renderDimensionDropdowns(dimensions, measure, mIndex, builtMeasures, selectedIndividualCells, builtMeasures.concat(userMeasures))}
                            <TableCell>
                                <Select
                                    value={getFormatString(aggregationSelections, measure)}
                                    id={`select-measure-format-${mIndex}`}
                                    fullWidth
                                    size="small"
                                    onChange={(e) => {
                                        e.preventDefault()
                                        let copiedFormatStringSelections = changeFormatString(e.target.value, aggregationSelections, measure)
                                        setAggregationSelections(copiedFormatStringSelections)
                                        forceUpdate()

                                        if (props.onSave) {
                                            props.onSave({
                                                "builtMeasures": builtMeasures,
                                                "aggregationSelections": copiedFormatStringSelections,
                                                "userMeasures": userMeasures
                                            })
                                        }
                                    }}
                                >
                                    <MenuItem value={''}></MenuItem>
                                    <MenuItem value={'0'}>0</MenuItem>
                                    <MenuItem value={'0.0'}>0.0</MenuItem>
                                    <MenuItem value={'0.00'}>0.00</MenuItem>
                                    <MenuItem value={'#,##0'}>#,##0</MenuItem>
                                    <MenuItem value={'#,##0.0'}>#,##0.0</MenuItem>
                                    <MenuItem value={'#,##0.0'}>#,##0.00</MenuItem>
                                    <MenuItem value={'#,##0;(#,##0)'}>#,##0;(#,##0)</MenuItem>
                                    <MenuItem value={'#,##0.00;(#,##0.00)'}>#,##0.00;(#,##0.00)</MenuItem>
                                    <MenuItem value={'$#,##0;($#,##0)'}>$#,##0;($#,##0)</MenuItem>
                                    <MenuItem value={'$#,##0.00;($#,##0.00)'}>$#,##0.00;($#,##0.00)</MenuItem>
                                    <MenuItem value={'0%'}>0%</MenuItem>
                                    <MenuItem value={'0.0%'}>0.0%</MenuItem>
                                    <MenuItem value={'0.00%'}>0.00%</MenuItem>
                                    <MenuItem value={'0"%"'}>0"%"</MenuItem>
                                    <MenuItem value={'0.0"%"'}>0.0"%"</MenuItem>
                                    <MenuItem value={'0.00"%"'}>0.00"%"</MenuItem>
                                </Select>
                            </TableCell>
                        </TableRow>
                    })}
                </TableBody>
            </Table>
        </TableContainer>
        <Stack direction="row" spacing={1} sx={{ marginTop: '30px' }}>
            <Chip label="Mandatory Undefined" sx={{ "backgroundColor": props.uiColors.MANDATORY_UNDEFINED }} />
            <Chip label="User Undefined"  sx={{ "backgroundColor": props.uiColors.USER_UNDEFINED }} />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ margin: '15px 0px' }}>
            <Chip label="Mandatory Defined" sx={{ "backgroundColor": props.uiColors.MANDATORY_DEFINED }} />
            <Chip label="User Defined"  sx={{ "backgroundColor": props.uiColors.USER_DEFINED }} />
        </Stack>
        <Button
            className="btn"
            variant="contained"
            onClick={(e) => {
                e.preventDefault()
                let bothMeasureGroups = builtMeasures.concat(userMeasures)
                let copiedUserMeasures = userMeasures
                let copiedAggregationSelections = aggregationSelections

                if (!bothMeasureGroups.includes(NEW_MEASURE_STRING)) {
                    copiedUserMeasures.push(NEW_MEASURE_STRING)

                    for (let x = 0; x < dimensions.length; x++) {
                        let currentDimension = dimensions[x]
                        if (currentDimension["id"] !== -1) {
                            copiedAggregationSelections.push({
                                "dimensionId": currentDimension["id"],
                                "dimensionName": currentDimension["name"],
                                "dimensionType": currentDimension["type"],
                                "aggregationType" : 0,
                                "measure": NEW_MEASURE_STRING,
                                "format": "",
                                "calcParameters": ""
                            })
                        }
                    }

                    setAggregationSelections(copiedAggregationSelections)
                    setUserMeasures(copiedUserMeasures)
                    forceUpdate()

                    if (props.onSave) {
                        props.onSave({
                            "builtMeasures": builtMeasures,
                            "aggregationSelections": copiedAggregationSelections,
                            "userMeasures": copiedUserMeasures
                        })
                    }
                }
            }}>
            Add User Measure
        </Button>
    </>
}

export default MeasuresDefinition